import * as Sentry from '@sentry/react';

// Where this bundle is actually RUNNING. A build-mode check can't tell a
// deployed production app from a preview of the same bundle on a laptop.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

function resolveEnvironment() {
  const explicit = import.meta.env.VITE_SENTRY_ENV;
  if (explicit) return explicit;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (LOCAL_HOSTS.has(host) || /^(10|127|192\.168)\./.test(host) || host.endsWith('.local')) {
    return 'local';
  }
  if (host.endsWith('.vercel.app')) return 'preview';
  if (host) return 'production';

  return import.meta.env.MODE || 'development';
}

export function initSentry() {
  Sentry.init({
    dsn: 'https://26b2a44ac7698f3de200bb8b65d5b82a@o4510306005680128.ingest.de.sentry.io/4510306016624720',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // TAS-324: MODE is the BUILD mode, not where the code is running. A local
    // `vite build && vite preview` reports MODE=production, so laptop testing
    // landed in Sentry tagged `production` (issue TASTED-GAME-4K, 36 events
    // from http://localhost:4173). Trust an explicit override first, then the
    // actual host, and only fall back to the build mode.
    environment: resolveEnvironment(),
    // Enable logging
    enableLogs: true,
    integrations: [
      // Send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Additional configuration
    beforeSend(event, hint) {
      // Filter out specific errors if needed
      // Example: if (error?.message?.includes('ResizeObserver')) return null;
      return event;
    },
    initialScope: {
      tags: {
        app: 'game',
      },
    },
  });
}

// Export Sentry for direct use
export { Sentry };

// Export logger for use throughout the app
// Note: logger is accessed via Sentry.logger, but we'll export it for convenience
export const logger = Sentry.logger;

// Supabase sometimes returns errors as plain objects (e.g. { code, details, hint, message }).
// Sentry will log these as "Object captured as exception..." unless we wrap them in an Error.
export function captureSupabaseException(err, options = {}) {
  if (!err) return;

  const { tags, extra, ...context } = options || {};

  const message =
    typeof err?.message === 'string' && err.message.trim().length > 0
      ? err.message
      : 'Supabase error';

  const wrapped =
    err instanceof Error ? err : new Error(`SupabaseError: ${message}`);

  Sentry.captureException(wrapped, {
    tags: tags || undefined,
    extra: {
      supabase: {
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
        message: err?.message,
      },
      ...(extra || {}),
      ...context,
    },
  });
}

// Helper functions for setting user context
export function setSentryUser(user) {
  Sentry.setUser({
    id: user?.id,
    username: user?.username,
    email: user?.email,
  });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

// Helper function for API calls with Sentry spans
export async function sentryFetch(url, options = {}) {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: `${options.method || 'GET'} ${url}`,
    },
    async () => {
      const response = await fetch(url, options);
      return response;
    }
  );
}

// Helper function for Supabase queries with Sentry spans
export async function sentrySupabaseQuery(queryFn, operation, name) {
  return Sentry.startSpan(
    {
      op: 'db.query',
      name: `${operation} ${name}`,
    },
    async () => {
      return await queryFn();
    }
  );
}

